import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";

import { getContacts, putContact, postContact } from "requesters/contactRequester";
import {
  createJobServiceContact,
  deleteContactFromJobService,
  updateContactType,
  markContactAs
} from "requesters/jobServiceContactRequester";

import _ from "lodash";

import SweetAlert from "react-bootstrap-sweetalert";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { CONTACT_TYPES, ACCESS_CONTACT_TYPE, NORMAL_CONTACT_TYPE } from "../../../../utils/constants";
import { getBaseUrl } from "../../reducers";

const EditContactModal = ({
  show, onHide, contacts, reloadData, jobServiceId
}) => {
  const baseUrl = useSelector(getBaseUrl);
  const [contactModalList, setContactModalList] = useState(_.values(contacts) || [])
  const [existingContact, setExistingContact] = useState(false)
  const [emailOptions, setEmailOptions] = useState([])
  const [alertTitle, setAlertTitle] = useState("")
  const [loadingContactIds, setLoadingContactIds] = useState([])
  const [contactErrors, setContactErrors] = useState({}) // Track errors for each contact by key/id
  const { clientTypes } = useSelector(state => state.options)

  const clientTypeOptions = clientTypes.map(v => ({
    value: v[1],
    label: v[0],
    title: "clientType",
  }));

  useEffect(() => {
    setContactModalList(_.values(contacts) || [])

    getContacts(baseUrl)
      .then(res => {
        if (res.status === 200) {
          const emailList = _.values(_.values(res.response?.contact)).map(contact => ({
            value: contact.id,
            label: contact.attributes.email,
            attributes: contact.attributes,
          }))
          setEmailOptions(emailList)
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }, [show])

  const searchContacts = ((query, callback) => {
    getContacts(baseUrl, { by_email: query })
      .then(res => {
        if (res.status === 200) {
          const emailList = _.values(_.values(res.response?.contact)).map(contact => ({
            value: contact.id,
            label: contact.attributes.email,
            attributes: contact.attributes,
          }))
          setEmailOptions(emailList)
          callback(emailList)
        }
      })
  })

  const updateContactMarkAs = (newContactModelList, index, mark_as_type) => {
    const params = {
      job_service_id: jobServiceId,
      contact_id: newContactModelList[index].id,
      mark_as: mark_as_type
    }

    markContactAs(baseUrl, params)
      .then(res => {
        if (res.status === 200) {
          newContactModelList[index].attributes = {
            ...newContactModelList[index].attributes,
            contactMarkedAs: mark_as_type
          }
          setContactModalList(newContactModelList)
          reloadData()
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }

  const handleChange = (e, index) => {
    const clonedList = [...contactModalList]
    const contactKey = clonedList[index]?.key || clonedList[index]?.id

    if (e?.target?.name) {
      const { name, value } = e.target
      if (name === "mark_as_access_contact") {
        const markAsType = e.target.checked ? ACCESS_CONTACT_TYPE : NORMAL_CONTACT_TYPE
        clonedList[index].attributes = { ...clonedList[index].attributes, contactMarkedAs: markAsType }
        if (!clonedList[index]?.key) {
          updateContactMarkAs(clonedList, index, markAsType)
        } else {
          setContactModalList(clonedList)
        }
      } else {
        clonedList[index].attributes = { ...clonedList[index].attributes, [name]: value }
        // Clear error when user changes email or phone
        if (name === "email" || name === "phoneNumber") {
          setContactErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors[contactKey]
            return newErrors
          })
        }
      }
      setContactModalList(clonedList)
    }

    if (e?.title === "clientType") {
      clonedList[index].attributes = {
        ...clonedList[index].attributes,
        clientTypeObject: e,
        clientTypeRaw: e.value,
        clientType: e.label
      }
      setContactModalList(clonedList)
    }

    if (e?.title === "contactType") {
      if (!clonedList[index]?.key) {
        const params = {
          job_service_id: jobServiceId,
          contact_id: clonedList[index].id,
          contact_type: e.value
        }

        updateContactType(baseUrl, params)
          .then(res => {
            if (res.status === 200) {
              clonedList[index].attributes = {
                ...clonedList[index].attributes,
                contactTypeObject: e
              }
              setContactModalList(_.values(contacts))
            }
          })
          // eslint-disable-next-line no-console
          .catch(err => console.log(err))
      } else {
        clonedList[index].attributes = {
          ...clonedList[index].attributes,
          contactTypeObject: e
        }
        setContactModalList(clonedList)
      }
    }
  }

  const getContact = async (email) => {
    try {
      const contactRes = await getContacts(baseUrl, { by_email: email })
      if (contactRes.status === 200) {
        const contacts = _.values(_.values(contactRes.response?.contact))
        return contacts.find(c => 
          c.attributes.email?.toLowerCase() === email.toLowerCase()
        )
      }
      return null
    } catch {
      return null
    }
  }

  const associateContactToJobService = async (contactId, currentContact, contactKey, index) => {
    const jobServiceContactNew = {
      job_service_id: jobServiceId,
      contact_id: contactId,
      contact_type: currentContact?.attributes?.contactTypeObject?.value || "participant",
      mark_as: currentContact?.attributes?.contactMarkedAs || NORMAL_CONTACT_TYPE
    }
    
    try {
      const jscRes = await createJobServiceContact(baseUrl, jobServiceContactNew)
      if (jscRes.status === 201) {
        setContactErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors[contactKey]
          return newErrors
        })
        
        // Update local state to mark contact as no longer new
        if (index !== undefined && currentContact?.isNewContact) {
          const updatedList = [...contactModalList]
          const { key, ...contactWithoutKey } = updatedList[index]
          updatedList[index] = {
            ...contactWithoutKey,
            id: contactId,
            isNewContact: false,
            isReadOnly: false
          }
          setContactModalList(updatedList)
        }
        
        reloadData()
        setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
        return true
      }
      return false
    } catch {
      setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
      return false
    }
  }

  const createNewContact = async (currentContact, contactKey, index) => {
    const contactData = {
      data: {
        contact: {
          email: currentContact?.attributes?.email,
          first_name: currentContact?.attributes?.firstName,
          last_name: currentContact?.attributes?.lastName,
          phone_number: currentContact?.attributes?.phoneNumber,
          company: currentContact?.attributes?.company || "",
          client_type: currentContact?.attributes?.clientTypeRaw || "",
          contact_type: currentContact?.attributes?.contactTypeObject?.value || "general"
        }
      }
    }
    
    try {
      const res = await postContact(baseUrl, contactData)
      if (res.status === 201) {
        const responseData = res.rawData?.json || res.rawData || res.response || {}
        
        if (responseData.existing_contact_found === true) {
          const errorField = responseData.identifier_type === "email" ? "email" : "phoneNumber"
          setContactErrors(prev => ({
            ...prev,
            [contactKey]: {
              field: errorField,
              message: "Contact already exists"
            }
          }))
          setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
          return
        }
        
        const contactId = responseData.contact_id
        
        if (contactId) {
          await associateContactToJobService(contactId, currentContact, contactKey, index)
        } else {
          const email = currentContact?.attributes?.email
          if (email) {
            const createdContact = await getContact(email)
            if (createdContact) {
              await associateContactToJobService(createdContact.id, currentContact, contactKey, index)
            } else {
              setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
            }
          } else {
            setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
          }
        }
      }
    } catch {
      setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
    }
  }

  const updateExistingContact = async (currentContact, contactKey) => {
    const data = {
      contact: {
        email: currentContact?.attributes?.email,
        first_name: currentContact?.attributes?.firstName,
        last_name: currentContact?.attributes?.lastName,
        phone_number: currentContact?.attributes?.phoneNumber,
        company: currentContact?.attributes?.company || "",
        client_type: currentContact?.attributes?.clientTypeRaw || "",
        mark_as: currentContact?.attributes?.contactMarkedAs || "",
        job_service_id: currentContact?.id || ""
      }
    }
    
    try {
      const res = await putContact(baseUrl, currentContact?.id, { data })
      if (res.status === 200) {
        reloadData()
        setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
      }
    } catch {
      setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
    }
  }

  const linkExistingContactToJobService = async (currentContact, index, contactKey) => {
    const jobServiceContactNew = {
      job_service_id: jobServiceId,
      contact_id: currentContact?.id,
      contact_type: currentContact?.attributes?.contactTypeObject?.value,
      mark_as: currentContact?.attributes?.contactMarkedAs
    }
    
    try {
      const res = await createJobServiceContact(baseUrl, jobServiceContactNew)
      if (res.status === 201) {
        reloadData()
        setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
        const newList = [...contactModalList]
        newList[index].isReadOnly = false
        setContactModalList(newList)
      }
    } catch {
      setLoadingContactIds(elements => elements.filter(id => id !== contactKey))
    }
  }

  const handleSave = (currentContact, index) => {
    const contactKey = currentContact?.key || currentContact?.id
    setLoadingContactIds(elements => [...elements, contactKey])
    
    if (currentContact?.isNewContact) {
      createNewContact(currentContact, contactKey, index)
      return
    }
    
    if (!currentContact?.key) {
      updateExistingContact(currentContact, contactKey)
    } else {
      linkExistingContactToJobService(currentContact, index, contactKey)
    }
  }

  const handleContactSelect = (e, index) => {
    const emailList = contactModalList?.find(item => item?.attributes?.email === e.label)

    if (emailList) {
      setAlertTitle("Contact already present!")
      setExistingContact(true)
    } else {
      const newList = [...contactModalList]

      newList[index] = {
        ...newList[index],
        attributes: {
          ...e.attributes,
          contactTypeObject: {
            value: "participant",
            label: "Participant",
            title: "contactType"
          }
        },
        id: e.value,
        isReadOnly: true
      }

      setContactModalList(newList)
    }
  }

  const addContact = () => {
    setContactModalList([...contactModalList, { key: Date.now() }])
  }

  const addNewContact = () => {
    setContactModalList([...contactModalList, { 
      key: Date.now(), 
      isNewContact: true,
      attributes: {
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        company: "",
        clientTypeObject: null,
        contactTypeObject: {
          value: "participant",
          label: "Participant",
          title: "contactType"
        },
        contactMarkedAs: NORMAL_CONTACT_TYPE
      }
    }])
  }

  const removeContactFromJobservice = contact => {
    // If contact has an id, it's been associated to the job service, so unassociate it
    if (contact?.id) {
      const params = {
        job_service_id: jobServiceId,
        contact_id: contact.id
      }

      deleteContactFromJobService(baseUrl, params)
        .then(res => {
          if (res.status === 200) {
            reloadData()
            const filteredList = contactModalList.filter(c => c.id !== contact?.id)
            setContactModalList(filteredList)
          }
        })
        // eslint-disable-next-line no-console
        .catch(err => console.log(err))
    } else {
      // If contact only has a key (new contact not yet saved/associated), just remove from list
      const filteredList = contactModalList.filter(c => c.key !== contact?.key)
      setContactModalList(filteredList)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <h2 className="modal-title">Edit Contacts</h2>
        </Modal.Title>
      </Modal.Header>
      <div className="modal-body">
        {!!contactModalList?.length && contactModalList.map((contact, index) => {
          const contactKey = contact?.key || contact?.id
          const isLoading = loadingContactIds.includes(contactKey)
          const contactError = contactErrors[contactKey]
          const emailError = contactError?.field === "email"
          const phoneError = contactError?.field === "phoneNumber"
          
          return (
            <div key={contact?.id || contact?.key}>
              <div className="row">
                <div className="col-sm-10">
                  <div className="form-group row">
                    <label className="col-sm-4 col-form-label">Email Address</label>
                    <div className="col-8 px-0">
                      <div className="row alight-items-center mb-2">
                        <div className="col-12">
                          {contact?.isNewContact ? (
                            <>
                              <input
                                className={`form-control ${emailError ? "border-danger" : ""}`}
                                name="email"
                                onChange={e => handleChange(e, index)}
                                placeholder="Email"
                                defaultValue={contact?.attributes?.email || ""}
                                type="email" />
                              {emailError && (
                                <div className="text-danger small mt-1">{contactError.message}</div>
                              )}
                            </>
                          ) : (
                            <AsyncSelect
                              name="email"
                              value={contact?.attributes?.emailObject}
                              defaultOptions={emailOptions}
                              isSearchable
                              options={emailOptions}
                              loadOptions={searchContacts}
                              onChange={e => { handleContactSelect(e, index) }} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-sm-4 col-form-label">Name*</label>
                    <div className="col-sm-8 p-0">
                      <div className="form-row">
                        <div className="col-6">
                          <input
                            className="form-control"
                            disabled={contact?.isReadOnly && !contact?.isNewContact}
                            name="firstName"
                            onChange={e => handleChange(e, index)}
                            placeholder="First name"
                            defaultValue={contact?.attributes?.firstName || ""}
                            type="text" />
                        </div>
                        <div className="col-6">
                          <input
                            className="form-control"
                            disabled={contact?.isReadOnly && !contact?.isNewContact}
                            name="lastName"
                            onChange={e => handleChange(e, index)}
                            placeholder="Last name"
                            defaultValue={contact?.attributes?.lastName || ""}
                            type="text" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-sm-4 col-form-label">
                      Phone Number
                    </label>
                    <div className="col-8 px-0">
                      <div className="row alight-items-center mb-2">
                        <div className="col-12">
                          <input
                            className={`form-control ${phoneError ? "border-danger" : ""}`}
                            disabled={contact?.isReadOnly && !contact?.isNewContact}
                            name="phoneNumber"
                            onChange={e => handleChange(e, index)}
                            placeholder="Phone Number"
                            defaultValue={contact?.attributes?.phoneNumber || ""}
                            type="tel" />
                          {phoneError && (
                            <div className="text-danger small mt-1">{contactError.message}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-sm-4 col-form-label">
                      Company
                    </label>
                    <input
                      className="form-control col-sm-8"
                      disabled={contact?.isReadOnly && !contact?.isNewContact}
                      name="company"
                      onChange={e => handleChange(e, index)}
                      defaultValue={contact?.attributes?.company || ""}
                      type="text" />
                  </div>
                  <div className="form-group row">
                    <label className="col-sm-4 col-form-label">Client Type</label>
                    <div className="col-8 px-0">
                      <div className="row alight-items-center mb-2">
                        <div className="col-12">
                          {contact?.isReadOnly && !contact?.isNewContact ? (
                            <input
                              className="form-control"
                              disabled
                              name="clientTypeRaw"
                              onChange={e => handleChange(e, index)}
                              defaultValue={contact?.attributes?.clientTypeObject?.label}
                              type="text" />
                          ) : (
                            <Select
                              name="clientTypeRaw"
                              onChange={e => handleChange(e, index)}
                              options={clientTypeOptions}
                              placeholder="Select"
                              value={contact?.attributes?.clientTypeObject} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-sm-4 col-form-label">Contact Type</label>
                    <div className="col-8 px-0">
                      <div className="row alight-items-center mb-2">
                        <div className="col-12">
                          <Select
                            name="contact_type"
                            onChange={e => handleChange(e, index)}
                            options={CONTACT_TYPES}
                            placeholder="Select"
                            value={contact?.attributes?.contactTypeObject} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-sm-4 col-form-label" htmlFor={`accessContact-${index}`}>Access Contact</label>
                    <div className="col-8 py-2">
                      <div className="row alight-items-center mb-2">
                        <div className="col-12">
                          <input
                            checked={contact?.attributes?.contactMarkedAs === ACCESS_CONTACT_TYPE}
                            className="w-20px h-20px"
                            type="checkbox"
                            name="mark_as_access_contact"
                            onChange={e => handleChange(e, index)} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-2 text-right">
                  <button
                    className="btn btn-link"
                    onClick={() => removeContactFromJobservice(contact)}
                    type="button">
                    Remove
                  </button>
                </div>
              </div>
              <div className="row justify-content-end pr-4">
                <button
                  disabled={
                    (!contact?.attributes?.email && !contact?.attributes?.phoneNumber) || 
                    !contact?.attributes?.firstName || 
                    !contact?.attributes?.lastName || 
                    isLoading
                  }
                  className="btn btn-red btn-lg"
                  onClick={() => handleSave(contact, index)}
                  type="button">
                  {contact?.isNewContact ? "Create & Add" : contact?.isReadOnly ? "Add" : "Save"}
                  {isLoading && <Spinner className="ml-1" animation="border" role="status" size="sm" />}
                </button>
              </div>
              <hr className="mt-2 mb-4" />
            </div>
          )
        })}

        <div className="d-flex justify-content-between">
          <div>
            <button
              className="btn btn-outlined-blue cursor-pointer mr-2"
              onClick={addContact}
              type="button">
              Link Existing Contact
            </button>
            <button
              className="btn btn-outlined-blue cursor-pointer"
              onClick={addNewContact}
              type="button">
              Create New Contact
            </button>
          </div>
          <button
            className="btn btn-light btn-lg"
            onClick={onHide}
            type="button">
            Cancel
          </button>
        </div>

        <hr />
      </div>
      <SweetAlert
        show={existingContact}
        type="error"
        title={alertTitle}
        closeOnClickOutside
        allowEscape={false}
        onConfirm={() => setExistingContact(false)} />
    </Modal>
  )
}

export default EditContactModal
